import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { QrCode } from "lucide-react";
import api from "@/lib/api";

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  userPhone: "",
  password: "",
  passwordConfirm: "",
  businessName: "",
  businessAddress: "",
  businessPhone: "",
  businessEmail: "",
  businessSubDomain: "",
  businessSector: "",
};

const FIELD_LABELS = {
  firstName: "Ad",
  lastName: "Soyad",
  email: "E-posta",
  userPhone: "Telefon",
  password: "Şifre",
  passwordConfirm: "Şifre Tekrar",
  businessName: "Restoran Adı",
  businessAddress: "Adres",
  businessPhone: "Restoran Telefonu",
  businessEmail: "Restoran E-posta",
  businessSubDomain: "Subdomain",
  businessSector: "İşletme Sektörü",
};


const BACKEND_PROPERTY_TO_FORM_FIELD = {
  Username: "email",
  Email: "email",
  UserPhone: "userPhone",
  Password: "password",
  FirstName: "firstName",
  LastName: "lastName",
  "Business.Name": "businessName",
  "Business.Address": "businessAddress",
  "Business.Phone": "businessPhone",
  "Business.Email": "businessEmail",
  "Business.SubDomain": "businessSubDomain",
  "Business.TypeConstantValue": "businessSector",
};

const BACKEND_MESSAGE_MAP = {
  "'Business Phone' must not be empty.": "Restoran telefonu boş bırakılamaz.",
  "'Business Name' must not be empty.": "Restoran adı boş bırakılamaz.",
  "'Business Sub Domain' must not be empty.": "Subdomain boş bırakılamaz.",
  "'Business Email' must not be empty.": "Restoran e-posta alanı boş bırakılamaz.",
  "'Email' must not be empty.": "E-posta boş bırakılamaz.",
  "'Password' must not be empty.": "Şifre boş bırakılamaz.",
  "Username sadece küçük harf, rakam, '.' ve '_' içerebilir.":
  "Kullanıcı adı sadece küçük harf, rakam, '.' ve '_' içerebilir.",
  "Telefon numarası 05XXXXXXXXX formatında olmalı":
  "Telefon numarası 05XXXXXXXXX formatında olmalı.",
};

function getInputClass(hasError) {
  return hasError
    ? "border-red-500 focus-visible:ring-red-500"
    : "";
}

function translateBackendMessage(item) {
  const rawMessage = item?.ErrorMessage || "";

  if (BACKEND_MESSAGE_MAP[rawMessage]) {
    return BACKEND_MESSAGE_MAP[rawMessage];
  }

  if (
    item?.PropertyName === "Business.Phone" &&
    rawMessage.toLowerCase().includes("must not be empty")
  ) {
    return "Restoran telefonu boş bırakılamaz.";
  }

  if (
    item?.PropertyName === "Business.Name" &&
    rawMessage.toLowerCase().includes("must not be empty")
  ) {
    return "Restoran adı boş bırakılamaz.";
  }

  if (
    item?.PropertyName === "Business.SubDomain" &&
    rawMessage.toLowerCase().includes("must not be empty")
  ) {
    return "Subdomain boş bırakılamaz.";
  }

  if (
  item?.PropertyName === "Username" &&
  rawMessage.toLowerCase().includes("must not be empty")
  ) {
    return "E-posta boş bırakılamaz.";
  }

  if (
    item?.PropertyName === "Email" &&
    rawMessage.toLowerCase().includes("must not be empty")
  ) {
    return "E-posta boş bırakılamaz.";
  }

  if (
    item?.PropertyName === "Password" &&
    rawMessage.toLowerCase().includes("must not be empty")
  ) {
    return "Şifre boş bırakılamaz.";
  }

  return rawMessage || "Bir doğrulama hatası oluştu.";
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [businessSectors, setBusinessSectors] = useState([]);
  const [constantsLoading, setConstantsLoading] = useState(false);

  useEffect(() => {
  const loadBusinessSectors = async () => {
    setConstantsLoading(true);

    try {
      const res = await api.getPublicConstants({
        type: "BUSINESS_SECTOR",
        pageSize: 1000,
      });

      setBusinessSectors(res?.items || []);
    } catch (error) {
      console.error("Sektörler yüklenemedi:", error);
      toast.error("İşletme sektörleri yüklenemedi.");
    } finally {
      setConstantsLoading(false);
    }
    };

    loadBusinessSectors();
  }, []);

  const fieldHasError = useMemo(
    () => (field) => Boolean(errors[field]),
    [errors]
  );

  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;

    setForm((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);

    if (field === "password" || field === "passwordConfirm") {
      clearFieldError("password");
      clearFieldError("passwordConfirm");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.businessName.trim()) {
      newErrors.businessName = "Lütfen restoran adını girin.";
    }

    if (!form.businessSector) {
      newErrors.businessSector = "Lütfen işletme sektörünü seçin.";
    }

    if (!form.email.trim()) {
      newErrors.email = "Lütfen kullanıcı e-posta adresini girin.";
    }

    if (!form.password.trim()) {
      newErrors.password = "Lütfen şifre girin.";
    }

    if (!form.passwordConfirm.trim()) {
      newErrors.passwordConfirm = "Lütfen şifre tekrarını girin.";
    }

    if (
      form.password.trim() &&
      form.passwordConfirm.trim() &&
      form.password !== form.passwordConfirm
    ) {
      newErrors.passwordConfirm = "Şifreler uyuşmuyor.";
    }

    const subdomainValue = form.businessSubDomain.trim().toLowerCase();

    if (!subdomainValue) {
      newErrors.businessSubDomain =
        "Lütfen subdomain girin. Bu alan QR kod ve menü linki için gereklidir.";
    } else if (!/^[a-z0-9-]+$/.test(subdomainValue)) {
      newErrors.businessSubDomain =
        "Subdomain sadece küçük harf, rakam ve tire içerebilir.";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0];
      toast.error(firstError);
      return false;
    }

    return true;
  };

  const applyBackendFailures = (failures = []) => {
    const nextErrors = {};
    const shownMessages = new Set();

    failures.forEach((item) => {
      const field = BACKEND_PROPERTY_TO_FORM_FIELD[item?.PropertyName];
      const translatedMessage = translateBackendMessage(item);

      if (field && !nextErrors[field]) {
        nextErrors[field] = translatedMessage;
      }

      if (!shownMessages.has(translatedMessage)) {
        toast.error(translatedMessage);
        shownMessages.add(translatedMessage);
      }
    });

    if (Object.keys(nextErrors).length > 0) {
      setErrors((prev) => ({
        ...prev,
        ...nextErrors,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      setErrors({});

      await api.register({
      email: form.email.trim(),
      password: form.password,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phone: form.userPhone.trim(),
      businessName: form.businessName.trim(),
      businessAddress: form.businessAddress.trim(),
      businessPhone: form.businessPhone.trim(),
      businessEmail: form.businessEmail.trim(),
      businessSubDomain: form.businessSubDomain.trim().toLowerCase(),
      businessSector: form.businessSector,
    });

      toast.success("Kayıt başarılı! Giriş yapılıyor...");

      await login(form.email.trim(), form.password);
      navigate("/admin");
    } catch (error) {
      console.error("REGISTER ERROR FULL:", error);

      const data = error?.response?.data;

      if (Array.isArray(data?.Failures) && data.Failures.length > 0) {
        applyBackendFailures(data.Failures);
        return;
      }

      if (typeof data === "string" && data.trim()) {
        toast.error(data);
        return;
      }

      if (data?.Detail) {
        toast.error(data.Detail);
        return;
      }

      if (data?.detail) {
        toast.error(data.detail);
        return;
      }

      if (data?.Title) {
        toast.error(data.Title);
        return;
      }

      if (data?.title) {
        toast.error(data.title);
        return;
      }

      toast.error("Kayıt sırasında bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const ErrorText = ({ field }) =>
    errors[field] ? (
      <p className="mt-1 text-xs text-red-600">{errors[field]}</p>
    ) : null;

  return (
    <div className="min-h-screen overflow-x-hidden bg-white flex flex-col">
      <header className="w-full bg-white/70 backdrop-blur border-b border-zinc-100">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-zinc-900 text-white grid place-items-center">
              <QrCode className="w-5 h-5" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-zinc-900">
                megriva QR Menü
              </div>
            </div>
          </Link>

          <nav className="flex shrink-0 items-center gap-2">
            <Link
              to="/"
              className="px-3 py-2 rounded-lg text-sm text-zinc-700 hover:bg-zinc-100 transition"
            >
              Ana Sayfa
            </Link>
            <Link
              to="/login"
              className="px-3 py-2 rounded-lg text-sm text-zinc-700 hover:bg-zinc-100 transition"
            >
              Giriş
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-2xl space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <QrCode className="w-12 h-12" />
            </div>
            <h1
              className="text-3xl font-bold tracking-tight"
              style={{ fontFamily: "Playfair Display, serif" }}
              data-testid="register-heading"
            >
              Restoran & Kullanıcı Kaydı
            </h1>
            <p className="text-zinc-600 mt-2">
              QR menü panelini kullanmak için hem kullanıcı hem de restoran
              bilgilerini doldurun.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-8"
            data-testid="register-form"
            noValidate
          >
            <div className="bg-zinc-50 border border-zinc-100 rounded-lg p-4 space-y-4">
              <h2 className="text-sm font-semibold text-zinc-700">
                Kullanıcı Bilgileri
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">{FIELD_LABELS.firstName}</Label>
                  <Input
                    id="firstName"
                    value={form.firstName}
                    onChange={handleChange("firstName")}
                    placeholder="Adınız"
                    className={getInputClass(fieldHasError("firstName"))}
                  />
                  <ErrorText field="firstName" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">{FIELD_LABELS.lastName}</Label>
                  <Input
                    id="lastName"
                    value={form.lastName}
                    onChange={handleChange("lastName")}
                    placeholder="Soyadınız"
                    className={getInputClass(fieldHasError("lastName"))}
                  />
                  <ErrorText field="lastName" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-posta</Label>
                  <Input
                    id="email"
                    type="text"
                    value={form.email}
                    onChange={handleChange("email")}
                    placeholder="ornek@mail.com"
                    className={getInputClass(fieldHasError("email"))}
                    required
                    data-testid="register-email-input"
                  />
                  <p className="text-[11px] text-zinc-500">
                    Geçerli bir e-posta adresi girin.
                  </p>
                  <ErrorText field="email" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="userPhone">{FIELD_LABELS.userPhone}</Label>
                  <Input
                    id="userPhone"
                    value={form.userPhone}
                    onChange={handleChange("userPhone")}
                    placeholder="05XXXXXXXXX"
                    className={getInputClass(fieldHasError("userPhone"))}
                  />
                  <ErrorText field="userPhone" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">{FIELD_LABELS.password}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange("password")}
                    placeholder="••••••••"
                    className={getInputClass(fieldHasError("password"))}
                    required
                    data-testid="register-password-input"
                  />
                  <ErrorText field="password" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="passwordConfirm">Şifre (Tekrar)</Label>
                  <Input
                    id="passwordConfirm"
                    type="password"
                    value={form.passwordConfirm}
                    onChange={handleChange("passwordConfirm")}
                    placeholder="••••••••"
                    className={getInputClass(fieldHasError("passwordConfirm"))}
                    required
                    data-testid="register-password-confirm-input"
                  />
                  <ErrorText field="passwordConfirm" />
                </div>
              </div>
            </div>

             <div className="bg-zinc-50 border border-zinc-100 rounded-lg p-4 space-y-4">
              <h2 className="text-sm font-semibold text-zinc-700">
                Restoran Bilgileri
              </h2>

              <div className="space-y-2">
                <Label htmlFor="businessName">
                  {FIELD_LABELS.businessName}
                </Label>
                <Input
                  id="businessName"
                  value={form.businessName}
                  onChange={handleChange("businessName")}
                  placeholder="Örneğin: Burger & Co."
                  className={getInputClass(fieldHasError("businessName"))}
                  required
                  data-testid="register-business-name-input"
                />
                <ErrorText field="businessName" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessSector">
                  İşletme Sektörü <span className="text-red-500">*</span>
                </Label>

                <select
                  id="businessSector"
                  value={form.businessSector}
                  onChange={(event) => {
                    setForm((prev) => ({
                      ...prev,
                      businessSector: event.target.value,
                    }));
                    clearFieldError("businessSector");
                  }}
                  disabled={constantsLoading}
                  required
                  className={`flex h-10 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 outline-none transition-colors focus:border-zinc-400 focus:ring-2 focus:ring-zinc-200 disabled:cursor-not-allowed disabled:opacity-50 ${getInputClass(
                    fieldHasError("businessSector")
                  )}`}
                >
                  <option value="" disabled>
                    {constantsLoading
                      ? "Sektörler yükleniyor..."
                      : "İşletmenizin sektörünü seçin"}
                  </option>

                  {businessSectors.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.name}
                    </option>
                  ))}
                </select>

                <ErrorText field="businessSector" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessAddress">
                  {FIELD_LABELS.businessAddress}
                </Label>
                <Input
                  id="businessAddress"
                  value={form.businessAddress}
                  onChange={handleChange("businessAddress")}
                  placeholder="Adres"
                  className={getInputClass(fieldHasError("businessAddress"))}
                />
                <ErrorText field="businessAddress" />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="businessPhone">
                    {FIELD_LABELS.businessPhone}
                  </Label>
                  <Input
                    id="businessPhone"
                    value={form.businessPhone}
                    onChange={handleChange("businessPhone")}
                    placeholder="05XXXXXXXXX"
                    className={getInputClass(fieldHasError("businessPhone"))}
                  />
                  <ErrorText field="businessPhone" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="businessEmail">
                    {FIELD_LABELS.businessEmail}
                  </Label>
                  <Input
                    id="businessEmail"
                    type="email"
                    value={form.businessEmail}
                    onChange={handleChange("businessEmail")}
                    placeholder="restoran@ornek.com"
                    className={getInputClass(fieldHasError("businessEmail"))}
                  />
                  <ErrorText field="businessEmail" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="businessSubDomain">Subdomain</Label>
                  <Input
                    id="businessSubDomain"
                    value={form.businessSubDomain}
                    onChange={handleChange("businessSubDomain")}
                    placeholder="orneksubdomain"
                    className={getInputClass(
                      fieldHasError("businessSubDomain")
                    )}
                    required
                  />
                  <p className="text-[11px] text-zinc-500">
                    Bu alan QR kod ve menü linki oluşturmak için zorunludur.
                  </p>
                  <ErrorText field="businessSubDomain" />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-zinc-900 hover:bg-zinc-800 h-11 rounded-full transition-all active:scale-95 disabled:opacity-70"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? "Kaydediliyor..." : "Hesap Oluştur"}
            </Button>
          </form>

          <p className="text-center text-sm text-zinc-600">
            Zaten bir hesabınız var mı?{" "}
            <Link
              to="/login"
              className="text-orange-500 hover:text-orange-600 font-medium"
              data-testid="register-login-link"
            >
              Giriş yapın
            </Link>
          </p>
        </div>
      </main>

      <footer className="border-t border-zinc-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="text-xs text-zinc-500">
            © {new Date().getFullYear()} megriva • QR Menü
          </div>

          <div className="flex items-center gap-4 text-xs">
            <Link to="/" className="text-zinc-600 hover:text-zinc-900">
              Ana Sayfa
            </Link>
            <Link to="/login" className="text-zinc-600 hover:text-zinc-900">
              Giriş Yap
            </Link>
            <Link to="/register" className="text-zinc-600 hover:text-zinc-900">
              Kayıt Ol
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}